import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Link,
  Navigate,
  Outlet,
  useLocation,
  useParams,
  useRouteError,
  useSearchParams,
} from 'react-router';
import { resolveNext } from '../lib/next';
import { ApiError } from '../api/client';
import { useMe, useSession, useSwitchWorkspace, useWorkspace, useWorkspaces } from '../api/queries';
import { GrantBanner } from '../features/staff/GrantBanner';
import { Alert, Loading } from '../components/ui';
import { buttonClass } from '../components/ui/button-class';
import { Copyright } from './AppShell';
import i18n, { toLocale } from '../i18n';
import { errorMessage } from '../i18n/labels';
import { DEFAULT_TIME_ZONE } from '../lib/format';
import { WorkspaceContext, type WorkspaceContextValue } from './hooks';

/** Signed-in area: redirects to sign-in (or the 2FA challenge) and applies the user's locale. */
export function RequireAuth() {
  const session = useSession();
  const location = useLocation();
  const me = useMe(session.data?.authenticated === true);
  const applied = useRef(false);

  useEffect(() => {
    // Per-user locale (FR-ACC-08) once per sign-in; later switches go through the locale menu.
    if (me.data && !applied.current) {
      applied.current = true;
      if (toLocale(i18n.language) !== me.data.locale) void i18n.changeLanguage(me.data.locale);
    }
  }, [me.data]);

  if (session.isPending) return <FullPageLoading />;
  if (session.isError) return <FullPageError error={session.error} />;
  if ('mfaPending' in session.data && session.data.mfaPending)
    return <Navigate to="/two-factor" replace />;
  if (!session.data.authenticated) {
    const next = location.pathname + location.search;
    return (
      <Navigate to={`/login${next !== '/' ? `?next=${encodeURIComponent(next)}` : ''}`} replace />
    );
  }
  if (me.isPending) return <FullPageLoading />;
  if (me.isError) return <FullPageError error={me.error} />;
  return <Outlet />;
}

/** Pages for signed-out users only. */
export function RequireGuest() {
  const session = useSession();
  const [params] = useSearchParams();
  if (session.isPending) return <FullPageLoading />;
  if (session.data?.authenticated) return <NextRedirect next={params.get('next')} />;
  return <Outlet />;
}

/** After sign-in: follow `?next=` only when it is safe (see lib/next.ts), else go home. */
export function NextRedirect({ next }: { next: string | null }) {
  const target = useMemo(() => resolveNext(next), [next]);
  useEffect(() => {
    if (target.kind === 'external') window.location.assign(target.url);
  }, [target]);
  if (target.kind === 'app') return <Navigate to={target.path} replace />;
  return <FullPageLoading />;
}

/** "/" → the last used workspace, the first one, or workspace creation. */
export function RootRedirect({ suffix = '' }: { suffix?: string }) {
  const me = useMe();
  const workspaces = useWorkspaces();
  if (workspaces.isPending || me.isPending) return <FullPageLoading />;
  if (workspaces.isError) return <FullPageError error={workspaces.error} />;
  const items = workspaces.data.items;
  const current = items.find((w) => w.id === me.data?.currentWorkspaceId) ?? items[0];
  if (!current) return <Navigate to="/workspaces/new" replace />;
  return <Navigate to={`/w/${current.id}${suffix}`} replace />;
}

export function WorkspaceLayout() {
  const { workspaceId = '' } = useParams();
  const ws = useWorkspace(workspaceId);
  const me = useMe();
  const switchWs = useSwitchWorkspace();
  const { mutate } = switchWs;
  // The API says how the caller reaches the workspace: as a member, or as Smoke staff reading
  // through an access grant (FR-ADM-03, read-only).
  const staffGrant = ws.data?.access === 'staff_readonly' ? ws.data.staffGrant : null;
  const member = ws.data?.access === 'member';

  useEffect(() => {
    // Remember the selection (FR-ACC-03).
    if (member && ws.data && me.data && me.data.currentWorkspaceId !== ws.data.id)
      mutate(ws.data.id);
  }, [member, ws.data, me.data, mutate]);

  const value = useMemo<WorkspaceContextValue | null>(
    () =>
      ws.data
        ? {
            id: ws.data.id,
            workspace: ws.data,
            timeZone: ws.data.timeZone || DEFAULT_TIME_ZONE,
            isAdmin: ws.data.role === 'admin' || ws.data.role === 'owner',
            isOwner: ws.data.role === 'owner',
          }
        : null,
    [ws.data],
  );

  if (ws.isPending) return <Loading />;
  if (ws.isError || !value) return <PageError error={ws.error} />;
  return (
    <WorkspaceContext.Provider value={value}>
      {staffGrant && (
        <div className="mb-5">
          <GrantBanner workspaceId={workspaceId} grant={staffGrant} inWorkspace />
        </div>
      )}
      <Outlet />
    </WorkspaceContext.Provider>
  );
}

function FullPageLoading() {
  return (
    <div className="flex min-h-dvh items-center justify-center">
      <Loading />
    </div>
  );
}

function FullPageError({ error }: { error: unknown }) {
  return (
    <div className="mx-auto flex min-h-dvh max-w-md items-center px-4">
      <PageError error={error} />
    </div>
  );
}

export function PageError({ error }: { error: unknown }) {
  const { t } = useTranslation();
  const notFound = error instanceof ApiError && error.status === 404;
  return (
    <div className="py-10">
      <Alert tone="danger" title={notFound ? t('errors.notFoundTitle') : t('errors.title')}>
        <p>{errorMessage(t, error)}</p>
        <Link to="/" className="link mt-2 inline-block">
          {t('common.backHome')}
        </Link>
      </Alert>
    </div>
  );
}

export function RouteError({ notFound }: { notFound?: boolean }) {
  const { t } = useTranslation();
  const error = useRouteError();
  if (error) console.error(error);
  return (
    <div className="mx-auto flex min-h-dvh max-w-md flex-col items-center justify-center gap-3 px-4 text-center">
      <p className="text-4xl font-semibold text-slate-300 dark:text-slate-700">
        {notFound ? '404' : '!'}
      </p>
      <h1 className="text-lg font-semibold">
        {notFound ? t('errors.notFoundTitle') : t('errors.title')}
      </h1>
      <p className="text-slate-600 dark:text-slate-400">
        {notFound ? t('errors.notFoundBody') : t('errors.unexpected')}
      </p>
      <Link to="/" className={buttonClass('secondary')}>
        {t('common.backHome')}
      </Link>
      <Copyright className="mt-6" />
    </div>
  );
}

/** FR-BIL-16: `/w/:id/billing` moved to the account; the query (checkout return) is kept. */
export function LegacyBillingRedirect() {
  const { search } = useLocation();
  return <Navigate to={`/account/billing${search}`} replace />;
}
